import type { Id, TicketListItem } from '../../api/types';

/**
 * Swimlanes grouped by parent story — `Group: Story` in Or's Jira (shot 6), where the board is a
 * header row `APFS-441 [CASE-659] … (18 subtasks)` with the columns drawn beneath it.
 *
 * ⭐ The parent is the HEADER, not a card. That is what the screenshot shows, and it also avoids the
 * one thing worse than a missing card — the same ticket drawn twice, once as a card and once as a
 * header, where changing it in one place leaves the other stale.
 * ⇒ The header carries the parent's own status control, so the parent is still movable.
 *
 * 🔴 THE RULE CARRIED FROM THE BACKLOG (`lib/ticket-tree.ts`): a sub-task whose parent is NOT in the
 * loaded window is still drawn — in the ungrouped swimlane, badged — never hidden. The board is a
 * WINDOW over a project (the API caps a page at 100 and Or's project holds 486), so "the parent is
 * not here" is routine, and a card that vanishes because of it reads as a deleted ticket.
 */

export type SwimlaneKind = 'story' | 'ungrouped';

export interface Swimlane {
  key: string;
  kind: SwimlaneKind;
  /** The parent story. `null` for the ungrouped lane. */
  parent: TicketListItem | null;
  tickets: TicketListItem[];
  /**
   * Sub-tasks in THIS lane whose parent is not in the window. Only ever non-empty on the ungrouped
   * lane; they are badged `↳` and offer "open parent", exactly as the backlog does.
   */
  detachedIds: ReadonlySet<Id>;
}

export interface SwimlaneResult {
  swimlanes: Swimlane[];
  /** How many cards are drawn out of place because their parent is outside the window. */
  detachedCount: number;
}

export const UNGROUPED_SWIMLANE_KEY = '__ungrouped';

/**
 * ⭐ Order is FIRST APPEARANCE in the input, not `updated_at` and not the key. The window arrives
 * `updated_at DESC`, so first-appearance means "the story someone touched most recently is on top",
 * which is stable across a re-render and does not reorder itself while a card is being dragged.
 */
export function buildSwimlanes(tickets: readonly TicketListItem[]): SwimlaneResult {
  const byId = new Map(tickets.map((ticket) => [ticket.id, ticket]));

  const childrenOf = new Map<Id, TicketListItem[]>();
  for (const ticket of tickets) {
    if (ticket.parent_id === null) continue;
    const siblings = childrenOf.get(ticket.parent_id);
    if (siblings) siblings.push(ticket);
    else childrenOf.set(ticket.parent_id, [ticket]);
  }

  /**
   * ⭐⭐ A ticket is a HEADER if it has a child in the window, and a CARD otherwise — never both.
   * db/009 now forbids a sub-task from being a parent, so a three-level chain should not arise; old
   * rows might still have one, and the alternative rules both fail on it. "Header only if it has no
   * parent" silently DROPS the grandchildren (their lane is never created), and "header as well as a
   * card" draws the middle ticket twice, where moving one copy leaves the other showing the old
   * status. This rule loses nothing and duplicates nothing.
   *
   * ⚠️ Restricted to parents that are IN the window: a parent id referenced by a child we can see but
   * whose own row we cannot is not a lane — treating it as one would create a lane with no header and
   * send its children into it, which is how they would disappear.
   */
  const storyIds = new Set([...childrenOf.keys()].filter((id) => byId.has(id)));

  const stories: Swimlane[] = [];
  const ungrouped: TicketListItem[] = [];
  const detached = new Set<Id>();

  for (const ticket of tickets) {
    if (storyIds.has(ticket.id)) {
      stories.push({
        key: `story:${ticket.id}`,
        kind: 'story',
        parent: ticket,
        // ⛔ A child that is itself a lane header is NOT also a card here — see the rule above. Its
        // own lane is where it lives, and one ticket drawn in two places is one that goes stale.
        tickets: (childrenOf.get(ticket.id) ?? []).filter((child) => !storyIds.has(child.id)),
        detachedIds: new Set<Id>(),
      });
      continue;
    }

    // A card whose parent heads a lane is drawn there and only there.
    if (ticket.parent_id !== null && storyIds.has(ticket.parent_id)) continue;

    // ⛔ Parent outside the window (or no parent at all). Drawn here rather than dropped.
    if (ticket.parent_id !== null) detached.add(ticket.id);
    ungrouped.push(ticket);
  }

  const swimlanes = [...stories];
  if (ungrouped.length > 0) {
    swimlanes.push({
      key: UNGROUPED_SWIMLANE_KEY,
      kind: 'ungrouped',
      parent: null,
      tickets: ungrouped,
      detachedIds: detached,
    });
  }

  return { swimlanes, detachedCount: detached.size };
}

/**
 * ⚠️ A nested sub-task — a child whose own parent is itself a child — would otherwise be counted in
 * its grandparent's lane AND its parent's. It is not: a lane holds only DIRECT children, and a child
 * that is itself a parent becomes a story lane of its own only when it has no parent in the window.
 * This function exists so that claim is stated once and can be asserted.
 */
export function ticketsInSwimlanes(result: SwimlaneResult): TicketListItem[] {
  return result.swimlanes.flatMap((swimlane) => swimlane.tickets);
}

/** Cards for one column of one swimlane. `statuses` comes from the column, never recomputed here. */
export function cardsInColumn(
  swimlane: Swimlane,
  statuses: readonly string[],
): TicketListItem[] {
  const wanted = new Set(statuses);
  return swimlane.tickets.filter((ticket) => wanted.has(ticket.status));
}
